import React from "react";
import "@/App.css";
import { 
  Phone, 
  MessageCircle, 
  Clock, 
  Shield, 
  Truck, 
  MapPin, 
  Star, 
  CheckCircle2,
  Zap,
  Users,
  Award,
  Timer,
  Car,
  Wrench,
  AlertTriangle,
  Navigation,
  HeartHandshake
} from "lucide-react";

const PHONE_PRIMARY = "81 99771-1873";
const PHONE_SECONDARY = "81 4105-5226";
const WHATSAPP_NUMBER = "5581997711873";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Preciso de um guincho urgente.`;
const PHONE_LINK_PRIMARY = `tel:+55${PHONE_PRIMARY.replace(/\s|-/g, '')}`;
const PHONE_LINK_SECONDARY = `tel:+55${PHONE_SECONDARY.replace(/\s|-/g, '')}`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_reboque-rapido-1/artifacts/tr86yqet_Captura%20de%20Tela%202026-03-18%20a%CC%80s%2008.00.11.png";
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_fed406ae-ef11-4dd0-907e-283fa63051ca/artifacts/01bzegh0_WhatsApp%20Video%202026-03-17%20at%2012.18.13.mp4";
const TRUCK_IMAGE = "https://customer-assets.emergentagent.com/job_reboque-rapido-1/artifacts/hd4jaw1n_WhatsApp%20Image%202026-03-17%20at%2012.16.35.jpeg";

// Services data
const services = [
  { icon: Truck, title: "Reboque Leve", description: "Para carros de passeio e veículos leves" },
  { icon: Car, title: "Reboque Utilitários", description: "Vans, pickups e veículos utilitários" },
  { icon: Shield, title: "Reboque Extra Pesado", description: "Caminhões e veículos de grande porte" },
  { icon: AlertTriangle, title: "SOS Veicular", description: "Socorro mecânico emergencial na estrada" },
  { icon: Users, title: "Táxi para Motorista", description: "Transporte para você enquanto cuidamos do seu carro" },
  { icon: Wrench, title: "Remoção de Veículos", description: "Remoção segura de qualquer local" },
  { icon: Clock, title: "Atendimento 24 Horas", description: "Disponíveis a qualquer hora, todos os dias" },
];

// Testimonials data
const testimonials = [
  { name: "Jackson Lima", text: "Atendimento 24 horas, o pessoal é testado!" },
  { name: "Genilson Gomes", text: "Aqui você encontra equipamento de qualidade e confiança, vem pra melhor! PDA REBOQUE 24h" },
  { name: "Matheus Cordon", text: "Empresa muito boa. Caminhão impecável, profissionais atenciosos e cuidadosos com o carro." },
  { name: "Igor Xavier", text: "Atendimento sensacional, equipe nota 10! Melhor da região disparado!" },
  { name: "Laiane Nobelia", text: "Atendimento excepcional, 24 horas, o melhor da nossa região." },
  { name: "Herculiana Barbosa", text: "Ótimo atendimento! Cliente sempre em primeiro lugar!" },
  { name: "Weliton Rebok", text: "Serviço de qualidade com rapidez e segurança, parabéns!" },
  { name: "Gabriela Silva", text: "Excelente atendimento, top." },
  { name: "Rubenildo Sebastião", text: "Atendimento com qualidade e agilidade." },
  { name: "Fabiano Guedes", text: "PDA REBOQUE show, top, parabéns." },
  { name: "Marcksuel Lopes", text: "Agilidade e responsabilidade na prestação do serviço." },
];

// Diferenciais data
const diferenciais = [
  { icon: Zap, title: "Atendimento Imediato", description: "Respondemos em minutos" },
  { icon: Users, title: "Equipe Especializada", description: "Profissionais treinados e experientes" },
  { icon: Truck, title: "Equipamentos Modernos", description: "Frota nova e bem mantida" },
  { icon: Shield, title: "Segurança Total", description: "Seu veículo protegido do início ao fim" },
  { icon: Timer, title: "Rapidez no Deslocamento", description: "Chegamos onde você está" },
  { icon: Award, title: "Preço Justo", description: "Sem surpresas, orçamento transparente" },
];

// Header Component
const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
    <div className="container mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
      <a href="/" className="flex items-center">
        <img 
          src={LOGO_URL} 
          alt="PDA Reboque 24HS" 
          className="h-14 md:h-16 w-auto"
          data-testid="header-logo"
        />
      </a>
      <div className="hidden sm:flex items-center gap-4">
        <a 
          href={PHONE_LINK_PRIMARY}
          className="flex items-center gap-2 text-[#0F172A] hover:text-[#DC2626] transition-colors font-bold"
        >
          <Phone className="w-4 h-4" />
          <span className="hidden md:inline">{PHONE_PRIMARY}</span>
        </a>
        <a 
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#25D366] text-white px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 hover:bg-[#128C7E] transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </a>
      </div>
    </div>
  </header>
);

// Hero Section
const HeroSection = () => (
  <section 
    data-testid="hero-section" 
    className="relative min-h-screen flex items-center justify-center hero-pattern overflow-hidden pt-20"
  >
    {/* Background Image */}
    <div className="absolute inset-0 z-0">
      <img 
        src={TRUCK_IMAGE} 
        alt="Guincho PDA Reboque em ação" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
    </div>

    <div className="relative z-10 container mx-auto px-4 md:px-6 py-20 md:py-32">
      <div className="max-w-4xl mx-auto text-center">
        {/* Urgency badge */}
        <div className="animate-fade-in-up opacity-0" style={{animationFillMode: 'forwards'}}>
          <span className="badge-urgent inline-flex items-center gap-2 mb-6">
            <Clock className="w-4 h-4" />
            DISPONÍVEL 24 HORAS
          </span>
        </div>

        {/* Main headline */}
        <h1 
          data-testid="hero-headline"
          className="font-black text-4xl sm:text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9] text-white mb-6 animate-fade-in-up opacity-0 stagger-1 drop-shadow-lg"
          style={{animationFillMode: 'forwards'}}
        >
          SEU VEÍCULO PAROU?
          <span className="block text-[#FACC15]">VOCÊ JÁ SABE QUEM CHAMAR!</span>
        </h1>

        {/* Subheadline */}
        <p 
          data-testid="hero-subheadline"
          className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto animate-fade-in-up opacity-0 stagger-2"
          style={{animationFillMode: 'forwards'}}
        >
          Atendimento rápido, seguro e disponível 24 horas em Pernambuco e região.
        </p>

        {/* CTA Buttons */}
        <div 
          className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in-up opacity-0 stagger-3"
          style={{animationFillMode: 'forwards'}}
        >
          <a 
            href={PHONE_LINK_PRIMARY}
            data-testid="hero-call-btn"
            className="btn-primary text-lg"
          >
            <Phone className="w-5 h-5" />
            Ligar Agora
          </a>
          <a 
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="btn-whatsapp text-lg"
          >
            <MessageCircle className="w-5 h-5" />
            Chamar no WhatsApp
          </a>
        </div>

        {/* Value props */}
        <div 
          className="flex flex-wrap justify-center gap-4 md:gap-8 animate-fade-in-up opacity-0 stagger-4"
          style={{animationFillMode: 'forwards'}}
        >
          {[
            { icon: Clock, text: "Atendimento 24h" },
            { icon: Zap, text: "Chegada rápida" },
            { icon: Users, text: "Profissionais experientes" },
            { icon: Award, text: "Preço justo" },
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-white">
              <CheckCircle2 className="w-5 h-5 text-[#FACC15]" />
              <span className="font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block">
      <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center pt-2">
        <div className="w-1.5 h-3 bg-[#FACC15] rounded-full animate-pulse"></div>
      </div>
    </div>
  </section>
);

// About/Video Section
const AboutSection = () => (
  <section data-testid="about-section" className="py-16 md:py-24 bg-[#0F172A]">
    <div className="container mx-auto px-4 md:px-6">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        {/* Video - Vertical format */}
        <div className="flex justify-center animate-fade-in-up opacity-0" style={{animationFillMode: 'forwards'}}>
          <div className="w-full max-w-[300px] md:max-w-[320px]">
            <video 
              data-testid="company-video"
              src={VIDEO_URL}
              controls
              playsInline
              preload="metadata"
              poster={TRUCK_IMAGE}
              className="w-full h-auto rounded-2xl shadow-2xl"
              style={{ aspectRatio: '9/16' }}
            >
              Seu navegador não suporta vídeos.
            </video>
          </div>
        </div>

        {/* Content */}
        <div className="text-white">
          <h2 
            className="font-bold text-3xl md:text-5xl uppercase tracking-tight mb-6 animate-fade-in-up opacity-0"
            style={{animationFillMode: 'forwards'}}
          >
            Sua Emergência é Nossa Prioridade
          </h2>
          <p 
            className="text-lg text-gray-300 mb-6 leading-relaxed animate-fade-in-up opacity-0 stagger-1"
            style={{animationFillMode: 'forwards'}}
          >
            A <strong className="text-[#FACC15]">PDA REBOQUE 24HS</strong> é referência em socorro veicular em Pernambuco e região. 
            Com equipamentos modernos e uma equipe altamente qualificada, garantimos atendimento rápido e seguro para você e seu veículo.
          </p>
          <p 
            className="text-lg text-gray-300 mb-8 leading-relaxed animate-fade-in-up opacity-0 stagger-2"
            style={{animationFillMode: 'forwards'}}
          >
            Nosso compromisso é oferecer um serviço de excelência, com transparência no preço e agilidade no atendimento. 
            Seja de dia ou de noite, estamos prontos para ajudar.
          </p>
          
          <div 
            className="flex flex-wrap gap-4 animate-fade-in-up opacity-0 stagger-3"
            style={{animationFillMode: 'forwards'}}
          >
            <a 
              href={PHONE_LINK_PRIMARY}
              className="btn-secondary"
              data-testid="about-call-btn"
            >
              <Phone className="w-5 h-5" />
              {PHONE_PRIMARY}
            </a>
            <a 
              href={PHONE_LINK_SECONDARY}
              className="btn-secondary"
            >
              <Phone className="w-5 h-5" />
              {PHONE_SECONDARY}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => (
  <section data-testid="services-section" className="py-16 md:py-24 bg-[#F8FAFC]">
    <div className="container mx-auto px-4 md:px-6">
      <div className="text-center mb-12">
        <span className="badge-highlight mb-4 inline-block">Serviços</span>
        <h2 className="font-bold text-3xl md:text-5xl uppercase tracking-tight text-[#0F172A] mb-4">
          Nossos Serviços
        </h2>
        <div className="section-divider mx-auto"></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {services.map((service, index) => (
          <div 
            key={index}
            data-testid={`service-card-${index}`}
            className="bg-white rounded-2xl p-6 border border-[#E2E8F0] card-hover animate-fade-in-up opacity-0"
            style={{animationFillMode: 'forwards', animationDelay: `${index * 0.1}s`}}
          >
            <div className="service-icon mb-4">
              <service.icon className="w-7 h-7" />
            </div>
            <h3 className="font-bold text-xl uppercase text-[#0F172A] mb-2">{service.title}</h3>
            <p className="text-[#475569]">{service.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Testimonials Section
const TestimonialsSection = () => (
  <section data-testid="testimonials-section" className="py-16 md:py-24 bg-white">
    <div className="container mx-auto px-4 md:px-6">
      <div className="text-center mb-12">
        <span className="badge-urgent mb-4 inline-block">Avaliações</span>
        <h2 className="font-bold text-3xl md:text-5xl uppercase tracking-tight text-[#0F172A] mb-4">
          O Que Nossos Clientes Dizem
        </h2>
        <div className="section-divider mx-auto"></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((testimonial, index) => (
          <div 
            key={index}
            data-testid={`testimonial-card-${index}`}
            className="testimonial-card animate-fade-in-up opacity-0"
            style={{animationFillMode: 'forwards', animationDelay: `${index * 0.05}s`}}
          >
            <div className="star-rating mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 inline-block fill-[#FACC15] text-[#FACC15]" />
              ))}
            </div>
            <p className="text-[#0F172A] mb-4 font-medium leading-relaxed">"{testimonial.text}"</p>
            <p className="text-[#475569] font-bold uppercase text-sm tracking-wide">— {testimonial.name}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Diferenciais Section
const DiferenciaisSection = () => (
  <section data-testid="diferenciais-section" className="py-16 md:py-24 bg-[#DC2626]">
    <div className="container mx-auto px-4 md:px-6">
      <div className="text-center mb-12">
        <h2 className="font-bold text-3xl md:text-5xl uppercase tracking-tight text-white mb-4">
          Por Que Escolher a PDA Reboque?
        </h2>
        <div className="w-20 h-1 bg-[#FACC15] mx-auto rounded-full"></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {diferenciais.map((item, index) => (
          <div 
            key={index}
            data-testid={`diferencial-card-${index}`}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-white text-center animate-fade-in-up opacity-0"
            style={{animationFillMode: 'forwards', animationDelay: `${index * 0.1}s`}}
          >
            <div className="w-16 h-16 rounded-full bg-[#FACC15] flex items-center justify-center mx-auto mb-4">
              <item.icon className="w-8 h-8 text-[#0F172A]" />
            </div>
            <h3 className="font-bold text-xl uppercase mb-2">{item.title}</h3>
            <p className="text-white/80">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// How It Works Section
const HowItWorksSection = () => (
  <section data-testid="how-it-works-section" className="py-16 md:py-24 bg-[#F8FAFC]">
    <div className="container mx-auto px-4 md:px-6">
      <div className="text-center mb-12">
        <span className="badge-highlight mb-4 inline-block">Simples e Rápido</span>
        <h2 className="font-bold text-3xl md:text-5xl uppercase tracking-tight text-[#0F172A] mb-4">
          Como Funciona
        </h2>
        <div className="section-divider mx-auto"></div>
      </div>

      <div className="max-w-3xl mx-auto">
        {[
          { step: 1, title: "Clique no botão do WhatsApp", description: "Toque no botão verde para iniciar a conversa conosco.", icon: MessageCircle },
          { step: 2, title: "Envie sua localização", description: "Compartilhe sua localização pelo WhatsApp para chegarmos mais rápido.", icon: Navigation },
          { step: 3, title: "Receba atendimento imediato", description: "Nossa equipe vai até você com agilidade e segurança.", icon: Truck },
        ].map((item, index) => (
          <div 
            key={index}
            data-testid={`step-${item.step}`}
            className="flex gap-6 mb-8 last:mb-0 animate-fade-in-up opacity-0"
            style={{animationFillMode: 'forwards', animationDelay: `${index * 0.2}s`}}
          >
            <div className="step-number">{item.step}</div>
            <div className="flex-1 pt-2">
              <h3 className="font-bold text-xl uppercase text-[#0F172A] mb-2">{item.title}</h3>
              <p className="text-[#475569]">{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="text-center mt-12">
        <a 
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="how-it-works-whatsapp-btn"
          className="btn-whatsapp text-xl inline-flex"
        >
          <MessageCircle className="w-6 h-6" />
          Chamar Guincho Agora
        </a>
      </div>
    </div>
  </section>
);

// Final CTA Section
const FinalCTASection = () => (
  <section data-testid="final-cta-section" className="py-16 md:py-24 bg-[#0F172A] relative overflow-hidden">
    {/* Background pattern */}
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTE2aC0yVjZoMnYxMnptLTE0IDhoLTR2LTJoNHYyem0xNiAwSDI2di0yaDEydjJ6Ii8+PC9nPjwvZz48L3N2Zz4=')]"></div>
    </div>

    <div className="container mx-auto px-4 md:px-6 relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="animate-fade-in-up opacity-0" style={{animationFillMode: 'forwards'}}>
          <span className="badge-urgent inline-flex items-center gap-2 mb-6">
            <AlertTriangle className="w-4 h-4" />
            EMERGÊNCIA?
          </span>
        </div>

        <h2 
          className="font-black text-3xl sm:text-4xl md:text-6xl uppercase tracking-tighter text-white mb-6 animate-fade-in-up opacity-0 stagger-1"
          style={{animationFillMode: 'forwards'}}
        >
          PRECISOU DE GUINCHO AGORA?
          <span className="block text-[#FACC15]">FALE COM A PDA REBOQUE 24HS!</span>
        </h2>

        <p 
          className="text-xl text-gray-300 mb-10 animate-fade-in-up opacity-0 stagger-2"
          style={{animationFillMode: 'forwards'}}
        >
          Atendimento imediato em Pernambuco e região. Não perca tempo!
        </p>

        <div 
          className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up opacity-0 stagger-3"
          style={{animationFillMode: 'forwards'}}
        >
          <a 
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-whatsapp-btn"
            className="btn-whatsapp text-xl"
          >
            <MessageCircle className="w-6 h-6" />
            WhatsApp
          </a>
          <a 
            href={PHONE_LINK_PRIMARY}
            data-testid="final-call-btn"
            className="btn-primary text-xl"
          >
            <Phone className="w-6 h-6" />
            Ligar Agora
          </a>
        </div>

        <div 
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-400 animate-fade-in-up opacity-0 stagger-4"
          style={{animationFillMode: 'forwards'}}
        >
          <a href={PHONE_LINK_PRIMARY} className="flex items-center gap-2 hover:text-white transition-colors">
            <Phone className="w-5 h-5" />
            <span className="font-bold">{PHONE_PRIMARY}</span>
          </a>
          <a href={PHONE_LINK_SECONDARY} className="flex items-center gap-2 hover:text-white transition-colors">
            <Phone className="w-5 h-5" />
            <span className="font-bold">{PHONE_SECONDARY}</span>
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer data-testid="footer-section" className="bg-[#0F172A] border-t border-white/10 py-12">
    <div className="container mx-auto px-4 md:px-6">
      <div className="grid md:grid-cols-3 gap-8 items-center">
        {/* Logo */}
        <div className="text-center md:text-left">
          <img 
            src={LOGO_URL} 
            alt="PDA Reboque 24HS" 
            className="w-40 mx-auto md:mx-0 mb-4"
          />
          <p className="text-gray-400 text-sm">
            Seu guincho de confiança em Pernambuco e região.
          </p>
        </div>

        {/* Contact */}
        <div className="text-center">
          <h4 className="font-bold text-white uppercase tracking-wide mb-4">Contato</h4>
          <div className="space-y-2">
            <a 
              href={PHONE_LINK_PRIMARY}
              className="flex items-center justify-center gap-2 text-gray-300 hover:text-[#FACC15] transition-colors"
            >
              <Phone className="w-4 h-4" />
              {PHONE_PRIMARY}
            </a>
            <a 
              href={PHONE_LINK_SECONDARY}
              className="flex items-center justify-center gap-2 text-gray-300 hover:text-[#FACC15] transition-colors"
            >
              <Phone className="w-4 h-4" />
              {PHONE_SECONDARY}
            </a>
            <a 
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-gray-300 hover:text-[#25D366] transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </div>

        {/* Location */}
        <div className="text-center md:text-right">
          <h4 className="font-bold text-white uppercase tracking-wide mb-4">Área de Atendimento</h4>
          <div className="flex items-center justify-center md:justify-end gap-2 text-gray-300">
            <MapPin className="w-4 h-4 text-[#DC2626]" />
            <span>Pernambuco e Região</span>
          </div>
          <div className="flex items-center justify-center md:justify-end gap-2 text-gray-300 mt-2">
            <Clock className="w-4 h-4 text-[#FACC15]" />
            <span>Atendimento 24 horas</span>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="mt-10 pt-6 border-t border-white/10 text-center">
        <p className="text-gray-500 text-sm">
          © {new Date().getFullYear()} PDA REBOQUE 24HS. Todos os direitos reservados.
        </p>
        <p className="text-gray-600 text-xs mt-2">
          Guincho 24 horas • Reboque 24h • Socorro veicular • Auto socorro urgente
        </p>
      </div>
    </div>
  </footer>
);

// Floating WhatsApp Button
const FloatingWhatsApp = () => (
  <a
    href={WHATSAPP_LINK}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="floating-whatsapp"
    className="floating-whatsapp"
    aria-label="Chamar no WhatsApp"
  >
    <MessageCircle className="w-8 h-8" />
  </a>
);

// Mobile Sticky Bar
const MobileStickyBar = () => (
  <div className="mobile-sticky-bar" data-testid="mobile-sticky-bar">
    <a 
      href={PHONE_LINK_PRIMARY}
      className="btn-primary flex-1 py-3 text-base"
      data-testid="mobile-call-btn"
    >
      <Phone className="w-5 h-5" />
      Ligar
    </a>
    <a 
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className="btn-whatsapp flex-1 py-3 text-base"
      data-testid="mobile-whatsapp-btn"
      style={{animation: 'none'}}
    >
      <MessageCircle className="w-5 h-5" />
      WhatsApp
    </a>
  </div>
);

// Main App Component
function App() {
  // Hide Emergent badge on mount
  React.useEffect(() => {
    const hideEmergentBadge = () => {
      const badges = document.querySelectorAll('a[href*="emergent"], div[class*="emergent"], [data-testid*="emergent"]');
      badges.forEach(el => {
        el.style.display = 'none';
        el.style.visibility = 'hidden';
      });
      // Also hide any fixed positioned element at bottom right that's not our components
      const fixedElements = document.querySelectorAll('body > div[style*="position: fixed"]');
      fixedElements.forEach(el => {
        if (!el.classList.contains('floating-whatsapp') && 
            !el.classList.contains('mobile-sticky-bar') &&
            !el.hasAttribute('data-testid')) {
          el.style.display = 'none';
        }
      });
    };
    
    hideEmergentBadge();
    // Run again after a short delay in case it loads later
    const timer = setTimeout(hideEmergentBadge, 1000);
    const timer2 = setTimeout(hideEmergentBadge, 3000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div className="App" data-testid="pda-reboque-app">
      <Header />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <TestimonialsSection />
      <DiferenciaisSection />
      <HowItWorksSection />
      <FinalCTASection />
      <Footer />
      <FloatingWhatsApp />
      <MobileStickyBar />
    </div>
  );
}

export default App;
